import type {
	OpenOptions,
	PrefilledTag,
	TriggerSurveyOptions,
	WeloopApi,
	WeloopEvent,
	WeloopStub,
} from "./types";

const isObject = (raw: unknown): raw is Readonly<Record<string, unknown>> =>
	typeof raw === "object" && raw !== null;

const isIdentifyPayload = (raw: unknown): raw is { readonly token: string } =>
	isObject(raw) && typeof raw["token"] === "string";

// The optional `{ event }` payload of `weloop("triggerSurvey", …)`. A missing
// or malformed payload reads as "no key", which `api.ts` refuses with a
// console warning – every survey fires by its trigger ID.
const parseTriggerSurveyOptions = (
	raw: unknown,
): TriggerSurveyOptions | undefined => {
	if (!isObject(raw)) {
		return undefined;
	}
	const { event } = raw;
	return typeof event === "string" && event !== "" ? { event } : undefined;
};

const isSwitchProjectPayload = (
	raw: unknown,
): raw is { readonly projectId: string } =>
	isObject(raw) && typeof raw["projectId"] === "string";

// Accepts either the shorthand `"name"` (sugar for an unlocked tag) or a
// full `PrefilledTag`-like object. `locked` defaults to `false` when missing;
// anything else is dropped. The returned shape always has `locked: boolean`
// set so downstream code never has to re-check.
const normalizePrefilledTag = (raw: unknown): PrefilledTag | undefined => {
	if (typeof raw === "string") {
		return { locked: false, value: raw };
	}
	if (!isObject(raw) || typeof raw["value"] !== "string") {
		return undefined;
	}
	const { locked } = raw;
	if (locked !== undefined && typeof locked !== "boolean") {
		return undefined;
	}
	return { locked: locked ?? false, value: raw["value"] };
};

// The views a host page may open. `survey` is left out: it only shows
// something with a `surveyId`, which comes from an email link alone.
const HOST_VIEWS: ReadonlySet<unknown> = new Set([
	"announcements",
	"feedback",
	"home",
	"notifications",
	"notifications-settings",
	"thread",
] satisfies OpenOptions["view"][]);

// Filters unknown input into a safe `OpenOptions`. Unknown fields and
// invalid tag entries are dropped so a stale or malicious embed can't
// inject arbitrary shapes into the iframe. The deep-link fields
// (`linkToken`, `projectSlug`, `surveyId`) never pass.
const parseOpenOptions = (raw: unknown): OpenOptions | undefined => {
	if (!isObject(raw)) {
		return undefined;
	}
	const result: { -readonly [K in keyof OpenOptions]: OpenOptions[K] } = {};
	const { announcementId, feedbackId, view } = raw;
	if (HOST_VIEWS.has(view)) {
		// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- membership in HOST_VIEWS narrows it to a view
		result.view = view as OpenOptions["view"];
	}
	if (typeof feedbackId === "string") {
		result.feedbackId = feedbackId;
	}
	if (typeof announcementId === "string") {
		result.announcementId = announcementId;
	}
	const { prefill: prefillRaw } = raw;
	if (isObject(prefillRaw)) {
		const prefill: {
			-readonly [K in keyof NonNullable<OpenOptions["prefill"]>]: NonNullable<
				OpenOptions["prefill"]
			>[K];
		} = {};
		const { message } = prefillRaw;
		if (typeof message === "string") {
			prefill.message = message;
		}
		const { tags: tagsRaw } = prefillRaw;
		if (Array.isArray(tagsRaw)) {
			const normalized: PrefilledTag[] = [];
			for (const entry of tagsRaw) {
				const tag = normalizePrefilledTag(entry);
				if (tag !== undefined) {
					normalized.push(tag);
				}
			}
			prefill.tags = normalized;
		}
		if (Object.keys(prefill).length > 0) {
			result.prefill = prefill;
		}
	}
	return result;
};

// The widget events match `WeloopEvent` – checked at runtime so we don't
// propagate stray event names from a stale embed snippet. Typed as a string
// set so membership checks accept `unknown`-narrowed input without an extra
// cast.
const WIDGET_EVENTS: ReadonlySet<string> = new Set([
	"close",
	"open",
	"ready",
	"submit",
	"survey",
] satisfies WeloopEvent[]);

const isWeloopEvent = (raw: unknown): raw is WeloopEvent =>
	typeof raw === "string" && WIDGET_EVENTS.has(raw);

const isHandler = (raw: unknown): raw is (payload?: unknown) => void =>
	typeof raw === "function";

// A call whose payload has the wrong shape, like `identify("<jwt>")` for
// `identify({ token: "<jwt>" })`, does nothing – say so instead of failing
// silently.
const warnShape = (method: string): void => {
	console.warn(`[weloop] ${method}: invalid argument`);
};

export const dispatch = (
	api: WeloopApi,
	args: readonly [string, ...unknown[]],
): void => {
	const [method, ...rest] = args;
	switch (method) {
		case "close": {
			api.close();
			return;
		}
		case "hideLauncher": {
			api.hideLauncher();
			return;
		}
		case "identify": {
			const [payload] = rest;
			if (isIdentifyPayload(payload)) {
				api.identify(payload);
			} else {
				warnShape(method);
			}
			return;
		}
		case "off": {
			const [event, handler] = rest;
			if (isWeloopEvent(event) && isHandler(handler)) {
				api.off(event, handler);
			}
			return;
		}
		case "on": {
			const [event, handler] = rest;
			if (isWeloopEvent(event) && isHandler(handler)) {
				api.on(event, handler);
			}
			return;
		}
		case "open": {
			const [payload] = rest;
			if (payload === undefined) {
				api.open();
				return;
			}
			const options = parseOpenOptions(payload);
			if (options === undefined) {
				warnShape(method);
			} else {
				api.open(options);
			}
			return;
		}
		case "showLauncher": {
			api.showLauncher();
			return;
		}
		case "switchProject": {
			const [payload] = rest;
			if (isSwitchProjectPayload(payload)) {
				api.switchProject(payload);
			} else {
				warnShape(method);
			}
			return;
		}
		case "triggerSurvey": {
			const [payload] = rest;
			api.triggerSurvey(parseTriggerSurveyOptions(payload));
			return;
		}
		default: {
			console.warn(`[weloop] unknown method: ${method}`);
		}
	}
};

const isArrayLike = (entry: unknown): entry is ArrayLike<unknown> =>
	typeof entry === "object" && entry !== null && "length" in entry;

// Replays calls queued on the pre-load stub (`window.weloop.q`) before the
// bundle arrived, dispatching each by method name.
//
// Entries arrive however the stub pushed them. The canonical analytics-snippet
// idiom does `push(arguments)`, so an entry can be an array-LIKE Arguments
// object rather than a real array – `Array.from` normalizes both. `q` is opaque
// host-page input, so the shape is validated here and `dispatch` validates each
// method's payload downstream.
export const drainQueue = (
	// oxlint-disable-next-line typescript-eslint/prefer-readonly-parameter-types -- WeloopStub.q is intentionally mutable; we mutate the stub's `loaded` flag on the same shape
	stub: WeloopStub,
	api: WeloopApi,
): void => {
	const queue = stub.q;
	if (!Array.isArray(queue)) {
		return;
	}
	for (const entry of queue) {
		if (!isArrayLike(entry)) {
			continue;
		}
		// oxlint-disable-next-line unicorn/prefer-spread -- spread throws on a non-iterable array-like; Array.from handles any length-bearing object, and `q` is untrusted host input we must not throw on
		const args = Array.from(entry);
		if (args.length > 0 && typeof args[0] === "string") {
			// oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- args[0] is verified to be a string above, matching the dispatch tuple
			dispatch(api, args as [string, ...unknown[]]);
		}
	}
};
